// Čím je zakázka určená. Projekt má vlastní název a produkci (departmenty se
// rozlišují až u nabídek a výrob); odběratelská zakázka je určená odběratelem.
// Pod každým druhem může viset jedna i mnoho výrob.
export type ProjectKind = 'project' | 'recurring' | 'oneoff';

export const PROJECT_KIND_META: Record<
  ProjectKind,
  { label: string; short: string; icon: string; hint: string; badge: string }
> = {
  project: {
    label: 'Projekt',
    short: 'Projekt',
    icon: '🎬',
    hint: 'Film, série, kampaň — má vlastní název a produkci, která ho objednává.',
    badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  },
  recurring: {
    label: 'Zakázka pro stálého odběratele',
    short: 'Pravidelný',
    icon: '🔁',
    hint: 'Stálý zákazník, který se vrací. Zakázku určuje odběratel, název je volitelný.',
    badge: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  },
  oneoff: {
    label: 'Jednorázová zakázka',
    short: 'Jednorázová',
    icon: '📦',
    hint: 'Jedna objednávka. Odběratel nemusí být v adresáři — stačí zakázku pojmenovat.',
    badge: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  },
};

export const PROJECT_KIND_ORDER: ProjectKind[] = ['project', 'recurring', 'oneoff'];

// Projekt je určený názvem, odběratelská zakázka odběratelem.
export const kindNeedsName = (kind: ProjectKind): boolean => kind === 'project';
// Department dává smysl jen u filmového projektu.
export const kindHasDepartment = (kind: ProjectKind): boolean => kind === 'project';

export type ProjectStatus =
  | 'draft'
  | 'quoted'
  | 'approved'
  | 'in_production'
  | 'done'
  | 'cancelled';

// České labely + barvy badge pro každý stav.
export const PROJECT_STATUS_META: Record<
  ProjectStatus,
  { label: string; badge: string }
> = {
  draft: { label: 'Koncept', badge: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300' },
  quoted: { label: 'Naceněno', badge: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  approved: { label: 'Schváleno', badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  in_production: { label: 'Ve výrobě', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  done: { label: 'Hotovo', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  cancelled: { label: 'Zrušeno', badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

export const PROJECT_STATUS_ORDER: ProjectStatus[] = [
  'draft',
  'quoted',
  'approved',
  'in_production',
  'done',
  'cancelled',
];

interface NamedRef {
  id: string;
  name: string;
}

export interface Project {
  id: string;
  kind: ProjectKind;
  code: string;
  codePrefix: string;
  codeYear: number;
  codeSequence: number;
  name: string;
  subtitle: string | null;
  clientId: string | null;
  departmentId: string | null;
  status: ProjectStatus;
  currency: string;
  deadline: string | null;
  notes: string | null;
  storageUrl: string | null;
  shareToken: string | null;
  shareExpiresAt: string | null;
  totalQuotedPrice: string | null;
  totalEstimatedCost: string | null;
  totalActualCost: string | null;
  client: (NamedRef & { codePrefix: string | null }) | null;
  department: NamedRef | null;
  _count: { propItems: number; quotes: number };
  createdAt: string;
  updatedAt: string;
}
